import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import { Send, Award } from 'lucide-react';

const QuizRoomPage = () => {
  const { sessionId } = useParams();
  const { getToken } = useAuth();
  const { user } = useUser();
  const [ws, setWs] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [scores, setScores] = useState({});
  const chatEndRef = useRef(null);

  // Build WebSocket URL
  const getWebSocketUrl = async () => {
    const token = await getToken();
    const userId = user.id;
    const userName = user.fullName || user.username || 'Student';
    // Note: WebSocket doesn't support headers, so we pass token as query param (optional, but verify on backend)
    // For security, you may add token to query but backend should validate.
    const wsUrl = `${process.env.REACT_APP_WS_URL || 'ws://localhost:8787'}/api/quiz/ws/${sessionId}?userId=${userId}&userName=${encodeURIComponent(userName)}`;
    return wsUrl;
  };

  useEffect(() => {
    let socket = null;
    const connect = async () => {
      const url = await getWebSocketUrl();
      socket = new WebSocket(url);
      socket.onopen = () => console.log('WebSocket connected');
      socket.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'chat' || data.type === 'question' || data.type === 'answer') {
          setMessages(prev => [...prev, { ...data, timestamp: Date.now() }]);
        } else if (data.type === 'scoreboard') {
          setScores(data.scores || {});
        }
      };
      socket.onerror = (err) => console.error('WebSocket error', err);
      setWs(socket);
    };
    connect();
    return () => {
      if (socket) socket.close();
    };
  }, [sessionId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = () => {
    if (!input.trim() || !ws) return;
    ws.send(JSON.stringify({ type: 'chat', message: input }));
    setInput('');
  };

  // For admin actions: you can add a separate input for awarding points (only shown if user is admin)
  // For simplicity, we'll just show chat and scoreboard.

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="bg-secondary text-white py-3 px-4 flex justify-between items-center">
        <h1 className="text-xl font-bold">Quiz Room #{sessionId}</h1>
        <div className="flex gap-2 text-sm">
          <Award size={18} className="text-accent" />
          <span>Leaderboard</span>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Chat area */}
        <div className="flex-1 flex flex-col p-4">
          <div className="flex-1 overflow-y-auto space-y-2 mb-4">
            {messages.map((msg, idx) => (
              <div key={idx} className={`p-2 rounded-lg max-w-xs ${msg.type === 'question' ? 'bg-accent text-white' : 'bg-surface shadow'}`}>
                <p className="text-xs font-semibold">{msg.userName || 'System'}</p>
                <p>{msg.message}</p>
                {msg.type === 'answer' && <span className="text-xs text-text-light">(answer)</span>}
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
              className="flex-1 border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Type a message..."
            />
            <button onClick={sendMessage} className="bg-primary text-white px-4 rounded-lg hover:bg-green-600">
              <Send size={18} />
            </button>
          </div>
        </div>

        {/* Leaderboard sidebar */}
        <div className="w-64 bg-surface border-l p-3 overflow-y-auto">
          <h2 className="font-bold text-secondary mb-2">🏆 Scoreboard</h2>
          {Object.entries(scores).length === 0 ? (
            <p className="text-text-light text-sm">No scores yet.</p>
          ) : (
            <ul className="space-y-1">
              {Object.entries(scores).map(([name, points]) => (
                <li key={name} className="flex justify-between text-sm">
                  <span>{name}</span>
                  <span className="font-semibold text-primary">{points}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Admin quick actions – optional, only visible if user is admin */}
      {/* You can add a panel to award points using ws.send(JSON.stringify({ type: 'admin_action', targetUserId: '...', points: 10 })) */}
    </div>
  );
};

export default QuizRoomPage;